// src/modules/academy/components/QuickActions.tsx

import React from 'react';
import Card from '../../../shared/components/ui/Card';
import { UserRole } from '../types';
import './QuickActions.css';

interface QuickActionsProps {
  userRoles: UserRole[];
}

interface QuickAction {
  id: string;
  title: string;
  description: string;
  color: string;
  relevantRoles: UserRole[];
  disabled?: boolean;
  badge?: string;
  onClick?: () => void;
}

const QuickActions: React.FC<QuickActionsProps> = ({ userRoles }) => {
  const allActions: QuickAction[] = [
    // Acciones para Participantes
    {
      id: 'browse-courses',
      title: 'Explorar Cursos',
      description: 'Descubre nuevos cursos disponibles',
      color: '#6366f1',
      relevantRoles: ['participant'],
      disabled: true
    },
    {
      id: 'register-congress',
      title: 'Inscribirse a un Congreso',
      description: 'Únete a próximos eventos académicos',
      color: '#3b82f6',
      relevantRoles: ['participant'],
      disabled: true,
      badge: 'Próximamente'
    },
    {
      id: 'join-competition',
      title: 'Unirse a Competencia',
      description: 'Participa en desafíos y concursos',
      color: '#f59e0b',
      relevantRoles: ['participant'],
      disabled: true
    },
    {
      id: 'view-certificates',
      title: 'Mis Certificados',
      description: 'Ver y descargar certificaciones',
      color: '#10b981',
      relevantRoles: ['participant'],
      disabled: true
    },

    // Acciones para Ponentes
    {
      id: 'submit-proposal',
      title: 'Enviar Propuesta',
      description: 'Proponer una charla o taller',
      color: '#10b981',
      relevantRoles: ['speaker'],
      disabled: true
    },
    {
      id: 'manage-sessions',
      title: 'Gestionar Sesiones',
      description: 'Administrar tus presentaciones',
      color: '#6366f1',
      relevantRoles: ['speaker'],
      disabled: true
    },
    {
      id: 'speaker-resources',
      title: 'Recursos para Ponentes',
      description: 'Materiales y guías de apoyo',
      color: '#3b82f6',
      relevantRoles: ['speaker'],
      disabled: true
    },

    // Acciones para Jueces
    {
      id: 'judge-panel',
      title: 'Panel de Evaluación',
      description: 'Acceder a competencias por evaluar',
      color: '#f59e0b',
      relevantRoles: ['judge'],
      disabled: true
    },
    {
      id: 'scoring-history',
      title: 'Historial de Evaluaciones',
      description: 'Revisar evaluaciones anteriores',
      color: '#3b82f6',
      relevantRoles: ['judge'],
      disabled: true
    },

    // Acciones generales
    {
      id: 'calendar',
      title: 'Mi Calendario',
      description: 'Ver agenda de eventos y sesiones',
      color: '#6366f1',
      relevantRoles: ['participant', 'speaker', 'judge'],
      disabled: true
    },
    {
      id: 'networking',
      title: 'Networking',
      description: 'Conectar con otros académicos',
      color: '#10b981',
      relevantRoles: ['participant', 'speaker', 'judge'],
      disabled: true,
      badge: 'Beta'
    },
    {
      id: 'support',
      title: 'Soporte Técnico',
      description: 'Obtener ayuda y asistencia',
      color: '#ef4444',
      relevantRoles: ['participant', 'speaker', 'judge'],
      disabled: true
    }
  ];

  // Filtrar acciones relevantes para los roles del usuario
  const relevantActions = allActions.filter(action => 
    action.relevantRoles.some(role => userRoles.includes(role))
  );

  // Organizar acciones por prioridad (máximo 6 para no saturar)
  const priorityActions = relevantActions.slice(0, 6);

  const handleActionClick = (action: QuickAction) => {
    if (action.disabled) {
      console.log(`${action.title} - Funcionalidad próximamente disponible`);
      return;
    }
    
    if (action.onClick) {
      action.onClick();
    } else {
      console.log(`Ejecutando acción: ${action.title}`);
    }
  };

  const getRoleBasedTitle = () => {
    if (userRoles.includes('speaker') && userRoles.includes('judge')) {
      return 'Panel de Ponente y Juez';
    } else if (userRoles.includes('speaker')) {
      return 'Panel de Ponente';
    } else if (userRoles.includes('judge')) {
      return 'Panel de Juez';
    } else {
      return 'Acciones Rápidas';
    }
  };

  const getRoleLabel = (role: UserRole): string => {
    switch (role) {
      case 'participant': return 'Participante';
      case 'speaker': return 'Ponente';
      case 'judge': return 'Juez';
      default: return 'Usuario';
    }
  };

  return (
    <Card className="academy-quick-actions" variant="glass" padding="large">
      <div className="quick-actions-header">
        <h2 className="section-title">{getRoleBasedTitle()}</h2>
        <p className="section-subtitle">
          Accede rápidamente a las funciones principales
        </p>
      </div>
      
      <div className="quick-actions-grid">
        {priorityActions.map((action) => (
          <button
            key={action.id}
            className={`quick-action-item ${action.disabled ? 'disabled' : ''}`}
            onClick={() => handleActionClick(action)}
            disabled={action.disabled}
            style={{
              '--action-color': action.color
            } as React.CSSProperties}
          >
            <div className="action-content">
              <div className="action-header">
                <h3 className="action-title">{action.title}</h3>
                {action.badge && (
                  <span className="action-badge">{action.badge}</span>
                )}
              </div>
              <p className="action-description">{action.description}</p>
            </div>
            
            {action.disabled && (
              <div className="action-status">
                <span className="status-text">Próximamente</span>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="roles-section">
        <div className="roles-header">
          <h4 className="roles-title">Mis Roles Activos</h4>
        </div>
        <div className="roles-list">
          {userRoles.map((role, index) => (
            <div key={index} className="role-chip">
              <span className="role-label">{getRoleLabel(role)}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="quick-actions-footer">
        <div className="footer-note">
          <span className="note-text">
            ¿Necesitas más funciones?
            <button className="link-button" disabled>
              Solicitar funcionalidad
            </button>
          </span>
        </div>
      </div>
    </Card>
  );
};

export default QuickActions;